import { useEffect, useMemo, useState } from "react";

// District shapes: public/maps/india_districts.json, made by the backend's
// build_district_map_data command (DataMeet Census 2011 districts, CC-BY 2.5 India).
const MAP_URL = `${process.env.PUBLIC_URL}/maps/india_districts.json`;
let mapCache = null;

const LOW = [0xe1, 0xf5, 0xee];
const HIGH = [0x08, 0x50, 0x41];
const NO_SALES = "#eef0f2";
const OUT_OF_VIEW = "#f7f7f8";

const shade = (t) => `rgb(${LOW.map((c, i) => Math.round(c + (HIGH[i] - c) * t)).join(",")})`;
const fmtNum = (n) => Number(n).toLocaleString("en-IN");
const fmtInr = (n) => `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const LABEL = "text-[11px] font-semibold uppercase tracking-wider text-gray-500";
const TH = "px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-gray-600 bg-gray-100 whitespace-nowrap";
const TD = "px-3 py-2 text-gray-700 whitespace-nowrap border-t border-gray-100";

// Bounding box of an SVG path made of M/L/Z commands
const pathBox = (d) => {
  const n = d.match(/-?[\d.]+/g).map(Number);
  let box = [Infinity, Infinity, -Infinity, -Infinity];
  for (let i = 0; i + 1 < n.length; i += 2) {
    box = [Math.min(box[0], n[i]), Math.min(box[1], n[i + 1]), Math.max(box[2], n[i]), Math.max(box[3], n[i + 1])];
  }
  return box;
};

// Product Sales counts units; Sales Stats counts orders
const PRODUCT_METRICS = [
  ["units", "Quantity", "Qty sold", "units"],
  ["revenue", "Revenue"],
];

// Label point: centroid of the district's largest ring
const labelPoint = (d) => {
  let best = null;
  for (const ring of d.split("M").filter(Boolean)) {
    const n = ring.match(/-?[\d.]+/g).map(Number);
    let a = 0, cx = 0, cy = 0;
    for (let i = 0; i + 1 < n.length; i += 2) {
      const j = (i + 2) % n.length;
      const cross = n[i] * n[j + 1] - n[j] * n[i + 1];
      a += cross;
      cx += (n[i] + n[j]) * cross;
      cy += (n[i + 1] + n[j + 1]) * cross;
    }
    if (a && (!best || Math.abs(a) > best.area)) best = { area: Math.abs(a), x: cx / (3 * a), y: cy / (3 * a) };
  }
  return best || { x: 0, y: 0 };
};

/**
 * data: { districts: [{key, state, district, revenue, <count>}], unmapped: {no_address, not_placed},
 *         total_units, unplaced_districts? } (e.g. GET /dashboard/product-sales/<sku>/districts)
 * metrics: toggle buttons, first one shown first; the non-revenue one is the count
 *          ([key, button label, tooltip label, word])
 * zoom / onZoomChange: optional, to control the selected state from the page
 * selectedDistrict / onDistrictClick: optional, clicking a district calls onDistrictClick(district)
 * sidePanel: optional, shown beside the map instead of the top districts table
 */
const DistrictSalesMap = ({
  data, loading, metrics = PRODUCT_METRICS, zoom: zoomProp, onZoomChange, selectedDistrict, onDistrictClick, sidePanel,
}) => {
  const [countKey, countLabel, countTip, countWord] = metrics.find(([key]) => key !== "revenue");
  const [shapes, setShapes] = useState(mapCache);
  const [mapError, setMapError] = useState(false);
  const [metric, setMetric] = useState(metrics[0][0]);
  const [zoomState, setZoomState] = useState("all");
  const zoom = zoomProp ?? zoomState;
  const setZoom = onZoomChange ?? setZoomState;
  const [hover, setHover] = useState(null);

  useEffect(() => {
    if (mapCache) return;
    fetch(MAP_URL)
      .then((r) => r.json())
      .then((json) => {
        mapCache = {
          ...json,
          districts: json.districts.map(([key, d]) => ({ key, state: key.split("|")[0], name: key.split("|")[1], d, box: pathBox(d), label: labelPoint(d) })),
        };
        setShapes(mapCache);
      })
      .catch(() => setMapError(true));
  }, []);

  const sales = useMemo(() => Object.fromEntries((data?.districts || []).map((d) => [d.key, d])), [data]);
  const inView = (s) => zoom === "all" || s.state === zoom;
  const value = (key) => sales[key]?.[metric] || 0;

  const stateOptions = useMemo(() => {
    const totals = {};
    (data?.districts || []).forEach((d) => (totals[d.state] = (totals[d.state] || 0) + d[countKey]));
    return Object.entries(totals).sort((a, b) => b[1] - a[1]);
  }, [data, countKey]);

  if (mapError) return <p className="text-sm text-red-600">Could not load the district map.</p>;
  if (!shapes || !data) return <p className="py-8 text-center text-sm text-gray-500">Loading map…</p>;

  const visible = (data.districts || []).filter((d) => zoom === "all" || d.state === zoom);
  const maxVal = Math.max(0, ...visible.map((d) => d[metric]));
  const viewTotal = visible.reduce((sum, d) => sum + d[countKey], 0);

  let viewBox = `0 0 ${shapes.width} ${shapes.height}`;
  let viewWidth = shapes.width;
  if (zoom !== "all") {
    const b = shapes.districts.filter((s) => s.state === zoom).reduce(
      (acc, s) => [Math.min(acc[0], s.box[0]), Math.min(acc[1], s.box[1]), Math.max(acc[2], s.box[2]), Math.max(acc[3], s.box[3])],
      [Infinity, Infinity, -Infinity, -Infinity]
    );
    viewBox = `${b[0] - 8} ${b[1] - 8} ${b[2] - b[0] + 16} ${b[3] - b[1] + 16}`;
    viewWidth = b[2] - b[0] + 16;
  }

  const fill = (s) => {
    if (!inView(s)) return OUT_OF_VIEW;
    const v = value(s.key);
    return v && maxVal ? shade(Math.sqrt(v / maxVal)) : NO_SALES;
  };
  const fmt = metric === "revenue" ? fmtInr : fmtNum;
  const unmapped = data.unmapped || {};
  const unmappedTotal = (unmapped.no_address || 0) + (unmapped.not_placed || 0);
  const hovered = hover && shapes.districts.find((s) => s.key === hover);
  // District names only once zoomed into a state
  const labelled = zoom === "all" ? [] : shapes.districts.filter((s) => s.state === zoom);
  const labelSize = Math.max(viewWidth / 55, 1.5);

  // India view: a click zooms into that state. Zoomed in: a click on one of its
  // districts opens it (if the page handles that); a click outside switches state.
  const clickDistrict = (s) => {
    if (zoom === s.state && onDistrictClick) {
      onDistrictClick({ key: s.key, state: s.state, district: s.name });
    } else if (zoom !== s.state) {
      setZoom(s.state);
      setHover(null);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end justify-end gap-3">
        <label className="block">
          <span className={`${LABEL} mb-1 block`}>State</span>
          <select
            value={zoom}
            onChange={(e) => {
              setZoom(e.target.value);
              setHover(null);
            }}
            className="h-9 min-w-[200px] rounded-lg border border-gray-300 bg-white px-2 text-sm text-gray-700"
          >
            <option value="all">All India</option>
            {stateOptions.map(([state, units]) => (
              <option key={state} value={state}>
                {state} ({fmtNum(units)})
              </option>
            ))}
          </select>
        </label>
        <div className="flex gap-1 rounded-lg bg-gray-100 p-1">
          {metrics.map(([m, label]) => (
            <button
              key={m}
              type="button"
              aria-pressed={metric === m}
              onClick={() => setMetric(m)}
              className={`rounded-md px-3 py-1.5 text-sm ${metric === m ? "bg-white font-semibold text-gray-900 shadow-sm" : "text-gray-600"}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className={`grid grid-cols-1 gap-4 lg:grid-cols-2 ${loading ? "opacity-50" : ""}`}>
        {/* Map */}
        <div className="relative rounded-xl border border-gray-100 bg-[#fbfdfc] p-2">
          <svg viewBox={viewBox} role="img" aria-label="India district map shaded by sales" className="block h-auto max-h-[560px] w-full">
            {shapes.districts.map((s) => (
              <path
                key={s.key}
                d={s.d}
                fill={fill(s)}
                stroke={hover === s.key || selectedDistrict === s.key ? "#111827" : inView(s) ? "#ffffff" : "#eceef0"}
                strokeWidth={hover === s.key || selectedDistrict === s.key ? 1.2 : 0.35}
                onMouseEnter={() => setHover(s.key)}
                onMouseLeave={() => setHover(null)}
                onClick={() => clickDistrict(s)}
                className="cursor-pointer"
              />
            ))}
            {/* State borders on top; they don't block district hover */}
            {shapes.state_borders && (
              <path
                d={shapes.state_borders}
                fill="none"
                stroke="#6b7280"
                strokeWidth={0.9}
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
                pointerEvents="none"
              />
            )}
            {labelled.map((s) => (
              <text
                key={`label-${s.key}`}
                x={s.label.x}
                y={s.label.y}
                fontSize={labelSize}
                textAnchor="middle"
                dominantBaseline="middle"
                fill="#111827"
                stroke="#ffffff"
                strokeWidth={labelSize / 5}
                paintOrder="stroke"
                pointerEvents="none"
              >
                {s.name}
              </text>
            ))}
          </svg>
          {hovered && (
            <div className="pointer-events-none absolute left-3 top-3 min-w-[170px] rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs text-gray-700 shadow-md">
              <p className="text-sm font-bold text-gray-900">{hovered.name}</p>
              <p className="mb-1 text-gray-500">{hovered.state}</p>
              <p>
                {countTip}: <b>{fmtNum(sales[hovered.key]?.[countKey] || 0)}</b>
              </p>
              <p>
                Revenue: <b>{fmtInr(sales[hovered.key]?.revenue || 0)}</b>
              </p>
              <p>
                Share: <b>{viewTotal ? (((sales[hovered.key]?.[countKey] || 0) / viewTotal) * 100).toFixed(1) : 0}%</b>
              </p>
            </div>
          )}
          <div className="flex items-center gap-2 px-1 pt-2 text-[11px] text-gray-600">
            <span>{fmt(0)}</span>
            <div className="h-2 flex-1 rounded-full" style={{ background: `linear-gradient(90deg, ${shade(0)}, ${shade(1)})` }} />
            <span>{fmt(maxVal)}</span>
            <span className="ml-2 inline-flex items-center gap-1">
              <span className="h-2.5 w-2.5 rounded-sm border border-gray-300" style={{ background: NO_SALES }} />
              No sales
            </span>
          </div>
          <p className="px-1 pt-1 text-[10px] text-gray-500">District boundaries: DataMeet, Census 2011 (CC-BY 2.5 India)</p>
        </div>

        {/* Top districts (or the page's own panel) */}
        {sidePanel || <div>
          <h3 className="mb-2 text-xs font-semibold text-gray-700">
            Top districts{zoom === "all" ? "" : ` in ${zoom}`} by {metric === "revenue" ? "revenue" : countLabel.toLowerCase()}
          </h3>
          <div className="relative overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className={`${TH} text-right`}>#</th>
                  <th className={`${TH} text-left`}>District</th>
                  <th className={`${TH} text-left`}>State</th>
                  <th className={`${TH} text-right`}>{countKey === "units" ? "Qty" : countLabel}</th>
                  <th className={`${TH} text-right`}>Revenue</th>
                  <th className={`${TH} text-left min-w-[110px]`}>Share</th>
                </tr>
              </thead>
              <tbody>
                {[...visible]
                  .sort((a, b) => b[metric] - a[metric])
                  .slice(0, 10)
                  .map((d, i) => (
                    <tr key={d.key}>
                      <td className={`${TD} text-right font-semibold text-gray-500`}>{i + 1}</td>
                      <td className={`${TD} font-semibold text-gray-900`}>{d.district}</td>
                      <td className={`${TD} text-gray-500`}>{d.state}</td>
                      <td className={`${TD} text-right`}>{fmtNum(d[countKey])}</td>
                      <td className={`${TD} text-right`}>{fmtInr(d.revenue)}</td>
                      <td className={TD}>
                        <div className="flex items-center gap-2">
                          <div className="h-1.5 flex-1 rounded-full bg-gray-100">
                            <div className="h-1.5 rounded-full bg-primary" style={{ width: `${maxVal ? (d[metric] / maxVal) * 100 : 0}%` }} />
                          </div>
                          <span className="w-10 text-right text-xs text-gray-600">
                            {viewTotal ? ((d[countKey] / viewTotal) * 100).toFixed(1) : 0}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
            {visible.length === 0 && <p className="py-6 text-center text-sm text-gray-500">No district sales with these filters.</p>}
          </div>

          {unmappedTotal > 0 && (
            <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              <b>{fmtNum(unmappedTotal)}</b> of {fmtNum(data.total_units)} {countWord} are not on the map:{" "}
              {fmtNum(unmapped.no_address || 0)} had no shipping address, {fmtNum(unmapped.not_placed || 0)} had no valid
              pincode or a district name we couldn't recognise
              {data.unplaced_districts?.length
                ? ` (e.g. ${data.unplaced_districts.slice(0, 3).map((d) => d.name).join(", ")})`
                : ""}
              .
            </div>
          )}
          <p className="mt-2 text-xs text-gray-500">
            {visible.length} districts with sales{zoom === "all" ? ` across ${stateOptions.length} states` : ` in ${zoom}`}.
          </p>
        </div>}
      </div>
    </div>
  );
};

export default DistrictSalesMap;
